import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CompareResponse, ShortlistResponse } from '@admitly/shared';
import { apiDelete, apiGet, apiPost } from '../lib/api.js';

/** The current user's shortlisted courses (visibility-gated, own-tenant). */
export function useShortlist() {
  return useQuery({
    queryKey: ['shortlist'],
    queryFn: () => apiGet<ShortlistResponse>('/shortlist'),
    staleTime: 30_000,
  });
}

/** Set of shortlisted course ids, for star state on any page. */
export function useShortlistIds(): Set<string> {
  const { data } = useShortlist();
  return new Set((data?.items ?? []).map((c) => c.id));
}

/** Toggle a course on/off the shortlist; refreshes the shortlist cache. */
export function useToggleShortlist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, saved }: { courseId: string; saved: boolean }) =>
      saved
        ? apiDelete<{ ok: true }>(`/shortlist/${courseId}`)
        : apiPost<{ ok: true }>(`/shortlist/${courseId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shortlist'] }),
  });
}

/** Hydrate the selected courses for the compare table (exactly the visible ones). */
export function useCompare(ids: string[]) {
  return useQuery({
    queryKey: ['compare', ids.join(',')],
    queryFn: () => apiGet<CompareResponse>(`/compare?ids=${encodeURIComponent(ids.join(','))}`),
    enabled: ids.length > 0,
    staleTime: 30_000,
  });
}
